import { useState, useContext, useMemo, MouseEvent, ChangeEvent } from 'react'
import {
  Box,
  Popover,
  Tooltip,
  useTheme,
  Theme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { SVGIcon } from 'src/images'
import {
  AdPlacementsContext,
  DEFAULT_LOW_IMP_DAU_THRESHOLD,
} from 'src/context/adPlacement'

const getUseStyles = (theme: Theme) =>
  makeStyles(() => {
    return {
      popover: {
        '& .MuiPaper-root': {
          minWidth: '300px',
          width: '300px',
          maxHeight: '394px',
          marginTop: '4px',
          borderRadius: '6px',
          padding: '16px 12px',
          background: theme.palette.custom.dialogBg,
        },
      },
    }
  })

const SortButton = () => {
  const theme: Theme = useTheme()
  const useStyles = getUseStyles(theme)
  const classes = useStyles()
  const { state, dispatch } = useContext(AdPlacementsContext)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const threshold =
    typeof state.lowImpDauThreshold === 'number'
      ? state.lowImpDauThreshold
      : DEFAULT_LOW_IMP_DAU_THRESHOLD
  const [thresholdInput, setThresholdInput] = useState<string>(String(threshold))
  // Keep local input in sync if state changes from elsewhere (e.g. reset).
  useMemo(() => {
    setThresholdInput(String(threshold))
    // eslint-disable-next-line
  }, [threshold])
  const commitThreshold = (raw: string) => {
    const parsed = parseFloat(raw)
    if (Number.isFinite(parsed) && parsed >= 0) {
      if (parsed !== threshold) {
        dispatch({ type: 'lowImpDauThreshold', payload: parsed })
      }
      setThresholdInput(String(parsed))
    } else {
      // Revert invalid input.
      setThresholdInput(String(threshold))
    }
  }
  const showList = useMemo(() => {
    // Always list every placement in the popover so the developer can see
    // exactly which ones are excluded by the threshold and re-enable them
    // individually if needed. Sort: above-threshold first, then below.
    const list = (state.chartList || []).slice()
    list.sort((a: any, b: any) => {
      const aLow = a.isLowImpDau ? 1 : 0
      const bLow = b.isLowImpDau ? 1 : 0
      if (aLow !== bLow) return aLow - bLow
      return (b.impDau || 0) - (a.impDau || 0)
    })
    return list
    // eslint-disable-next-line
  }, [state.chartList])
  const hiddenLowCount = useMemo(() => {
    if (state.showLowImpDau) return 0
    return (state.chartList || []).filter((row: any) => row.isLowImpDau).length
    // eslint-disable-next-line
  }, [state.chartList, state.showLowImpDau])
  const handleClick = (event: MouseEvent<unknown>, value: string) => {
    const selectedIndex = state.checkedChartLine.indexOf(value)
    let newSelected: string[] = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(state.checkedChartLine, value)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(state.checkedChartLine.slice(1))
    } else if (selectedIndex === state.checkedChartLine.length - 1) {
      newSelected = newSelected.concat(state.checkedChartLine.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        state.checkedChartLine.slice(0, selectedIndex),
        state.checkedChartLine.slice(selectedIndex + 1)
      )
    }
    dispatch({
      type: 'checkedChartLine',
      payload: newSelected,
    })
  }

  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '24px',
          height: '24px',
          padding: '3px',
          cursor: 'pointer',
        }}
        aria-describedby={id}
        onClick={handleOpen}
      >
        <Tooltip title="Filter graph legends" arrow placement="top">
          <Box>
            <SVGIcon.SettingSVG
              color={theme.palette.text.primary}
              style={{ verticalAlign: 'text-top', lineHeight: '18px' }}
            />
          </Box>
        </Tooltip>
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.popover}
        sx={{ zIndex: 1999 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 4px 8px 4px',
            borderBottom: `1px solid ${theme.palette.divider}`,
            marginBottom: '4px',
            gap: '8px',
          }}
        >
          <Tooltip
            title={`Show placements with Imp/DAU below ${threshold}. These are typically old or experimental placements with negligible traffic.`}
            arrow
            placement="top"
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={!!state.showLowImpDau}
                  onChange={(_, checked) =>
                    dispatch({ type: 'showLowImpDau', payload: checked })
                  }
                />
              }
              label={
                <Box
                  sx={{
                    fontSize: 12,
                    color: theme.palette.custom.inputLabel,
                  }}
                >
                  {`Show low Imp/DAU`}
                  {hiddenLowCount > 0 && !state.showLowImpDau
                    ? ` · ${hiddenLowCount} hidden`
                    : ''}
                </Box>
              }
              sx={{ marginLeft: 0, marginRight: 0 }}
            />
          </Tooltip>
          <Tooltip
            title="Imp/DAU threshold. Placements at or above this value are shown by default."
            arrow
            placement="top"
          >
            <TextField
              size="small"
              type="number"
              label="Threshold"
              value={thresholdInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setThresholdInput(e.target.value)
              }
              onBlur={() => commitThreshold(thresholdInput)}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  commitThreshold(thresholdInput)
                  ;(e.target as HTMLInputElement).blur()
                }
              }}
              inputProps={{
                step: 0.001,
                min: 0,
                style: { fontSize: 12, padding: '6px 8px', width: 70 },
              }}
              InputLabelProps={{ shrink: true, style: { fontSize: 12 } }}
              variant="outlined"
            />
          </Tooltip>
        </Box>
        <TableContainer>
          <Table aria-label="simple table">
            <TableBody>
              {showList.map((row: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  onClick={event => handleClick(event, row.value)}
                  role="checkbox"
                  aria-checked={state.checkedChartLine.includes(row.value)}
                  tabIndex={-1}
                  selected={state.checkedChartLine.includes(row.value)}
                  style={{
                    cursor: 'pointer',
                    background: theme.palette.custom.chartTableBg,
                    opacity: row.isLowImpDau ? 0.55 : 1,
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    style={{ padding: 0, width: 42 }}
                  >
                    <Checkbox
                      color="primary"
                      checked={state.checkedChartLine.includes(row.value)}
                      style={{ color: '#7487AA' }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ padding: 0, width: 20 }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '10px',
                        background: row.color,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{
                      color: theme.palette.custom.inputLabel,
                      fontSize: 12,
                      padding: '10px 12px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>{row.label}</Box>
                      {typeof row.impDau === 'number' && (
                        <Box
                          sx={{
                            fontSize: 11,
                            opacity: 0.7,
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.impDau.toFixed(4)}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Popover>
    </>
  )
}
export default SortButton
